import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PatientCard from "./PatientCard";
import { isVisitCompleted } from "./utils";
import { getPatientPageVisitPathSegment } from "../../utils/visitPathUtils";
import { sortObjectsByTime } from "../../utils/utils";
import { foreignSchemaOf } from "../../utils/rowSchema";
import { supabaseSchema } from "../../components/supabaseClient";
import { usePriorVisitsBadge } from "../../hooks/medstudent/usePriorVisitsBadge";

// notes-c schedule rows expose `id` (uuid). Use the same helper SchedulesTable uses
// so the active highlight + react `key` line up with what we navigate to.
function scheduleKey(s) {
  return getPatientPageVisitPathSegment(s) || s?.id || s?.uuid || s?.EHR_ID;
}

// Local-date ISO (YYYY-MM-DD). Mirrors MedStudentClinicPage.todayIso —
// new Date().toISOString() returns UTC and shifts to the wrong day for
// users west of UTC after their local midnight.
function todayIsoLocal() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

// Format the provider's name into "Dr. <Last>" form for the panel header.
// Handles the common patterns: "First Last", "Last, First", "Dr. First Last".
function formatDrName(name) {
  if (!name) return null;
  const s = name.trim();
  if (/^dr\.?\s/i.test(s)) return s; // already "Dr. ..."
  if (s.includes(",")) {
    // "Last, First"
    return `Dr. ${s.split(",")[0].trim()}`;
  }
  const parts = s.split(/\s+/);
  return `Dr. ${parts[parts.length - 1]}`;
}

export default function PatientListPanel({
  date,
  schedules,
  isLoading,
  error,
  onRetry,
  activeUuid,
  onCollapse,
  providerName,
  providerInitials,
  viewerTimezone,
}) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const isToday = date === todayIsoLocal();
  const headerLabel = isToday ? "TODAY'S SCHEDULE" : "SCHEDULE";

  // Sort by appointment time (earliest → latest) so both ACTIVE and COMPLETED
  // buckets are chronological. sortObjectsByTime converts each row's stored
  // wall-clock time_ into the VIEWER's timezone (via timezone_source +
  // viewerTimezone) before comparing — the SAME conversion PatientCard uses to
  // DISPLAY the time — so the order always matches what's shown on screen, even
  // when the schedule owner is in a different timezone than the viewer. Rows
  // with no parseable time_ are appended at the end. We copy the array because
  // the sort is in-place and `schedules` is owned by the hook.
  const sorted = useMemo(() => {
    const rows = (schedules || []).filter(
      (s) => s && typeof s.time_ === "string",
    );
    const unscheduled = (schedules || []).filter(
      (s) => !s || typeof s.time_ !== "string",
    );
    const sortedRows = sortObjectsByTime([...rows], "time_", {
      dateKey: "date_",
      viewerTimezone,
    });
    return [...sortedRows, ...unscheduled];
  }, [schedules, viewerTimezone]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((s) =>
      (s.patient_name || "").toLowerCase().includes(q),
    );
  }, [sorted, query]);

  // Bucket visits: a row is "completed" once its appointment time has passed
  // AND a recording has been saved against it. Anything else (future visit,
  // past visit with no recording, recording but appointment still upcoming)
  // stays in ACTIVE. See isVisitCompleted in ./utils for the exact rule.
  const { active, completed } = useMemo(() => {
    const now = new Date();
    const a = [];
    const c = [];
    for (const s of filtered) {
      if (isVisitCompleted(s, now)) c.push(s);
      else a.push(s);
    }
    return { active: a, completed: c };
  }, [filtered]);

  // Per-row "has prior visit on file" check, shared with PatientCard so the
  // ACTIVE list can badge each visit that the same patient has been seen
  // for before. Skipped for roster rows (no date_ keying) — see the hook.
  const { hasPriorVisit } = usePriorVisitsBadge(filtered);

  // A row from a linked provider lives in ANOTHER schema, so the chart has to
  // be told which one — `/clinic/:uuid` alone resolves against the viewer's
  // home schema, finds no patient row there, and renders blank. Same handoff
  // ResultsTable does: `?schema=` for a refresh-proof deep link, plus nav state
  // so the page has it before the first render. A home-schema row is
  // unchanged: no param, no state.
  const handleClick = (uuid, row) => {
    // foreignSchemaOf, not `row._schema`: the fan-out tags EVERY row, the
    // viewer's own included, so keying on the raw tag would append
    // `?schema=<home>` to every link in the sidebar.
    const rowSchema = foreignSchemaOf(row, supabaseSchema) || "";
    const params = new URLSearchParams();
    if (dateParam) params.set("date", dateParam);
    if (rowSchema) params.set("schema", rowSchema);
    const qs = params.toString();
    // `rowData` is the row this card just rendered. The chart seeds its header
    // from it so switching patients shows the NEW patient immediately, instead
    // of holding the previous one's schema, owner and document path until the
    // fetch lands. Ignored unless its id matches the uuid.
    navigate(`/clinic/${uuid}${qs ? `?${qs}` : ""}`,
             { state: { rowData: row, ...(rowSchema ? { _schema: rowSchema } : {}) } });
  };

  const drName = formatDrName(providerName);
  const ownerLabel = drName ? `${drName.toUpperCase()}'S LIST` : headerLabel;

  return (
    <aside className="w-72 shrink-0 border-r border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] flex flex-col">
      <div className="p-4 pb-3">
        {/* Owner identity — makes it explicit whose schedule you're looking
            at. Click the chevron to view other providers' schedules
            (admin/staff context); for now this is a stub that surfaces
            the affordance without breaking RLS. Real provider-switch
            wiring lands when the office_providers roster is populated. */}
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold tracking-[0.16em] text-slate-500 dark:text-slate-400 truncate">
              {ownerLabel}
            </div>
            <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
              {date}
            </div>
          </div>
          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              aria-label="Collapse patient list"
              title="Collapse patient list"
              className="-mt-0.5 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
        </div>
        {/* View-as switcher — gated on having a provider context. Disabled
            placeholder until the office_providers roster API wires up so
            admins/staff can preview another provider's schedule. */}

        <div className="relative">
          <svg
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"
            />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search patients..."
            className="w-full bg-slate-100 dark:bg-slate-800/40 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-400"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {error && (
          <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
            Couldn't load schedule.
            <button
              onClick={onRetry}
              className="ml-2 text-blue-600 dark:text-blue-300 underline"
            >
              Retry
            </button>
          </div>
        )}
        {!error && isLoading && (
          <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
            Loading…
          </div>
        )}
        {!error && !isLoading && (schedules || []).length === 0 && (
          <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
            No visits scheduled for {date}.
          </div>
        )}
        {active.length > 0 && (
          <>
            <div className="text-[10px] font-bold tracking-[0.16em] text-slate-400 dark:text-slate-500 px-2 mb-2 mt-2">
              ACTIVE
            </div>
            {active.map((s) => (
              <PatientCard
                key={scheduleKey(s)}
                patient={s}
                active={scheduleKey(s) === activeUuid}
                hasPriorVisit={hasPriorVisit(s)}
                viewerTimezone={viewerTimezone}
                onClick={handleClick}
              />
            ))}
          </>
        )}
        {completed.length > 0 && (
          <>
            <div className="text-[10px] font-bold tracking-[0.16em] text-slate-400 dark:text-slate-500 px-2 mb-2 mt-5">
              COMPLETED
            </div>
            {completed.map((s) => (
              <PatientCard
                key={s.uuid}
                patient={s}
                active={s.uuid === activeUuid}
                completed
                viewerTimezone={viewerTimezone}
                onClick={handleClick}
              />
            ))}
          </>
        )}
      </div>
    </aside>
  );
}
